import { Component } from '@theme/component';

const COPIED_DURATION = 3000;

/**
 * Copies the `text-to-copy` attribute to the clipboard on `on:click="/copyToClipboard"`.
 *
 * Optional, all additive:
 * - `ref="copySuccessMessage"`: un-hidden after a successful copy (gift_card.liquid).
 * - `ref="liveRegion"`: its text is set to `data-success-message` or `data-error-message`.
 * - `ref="copyTarget"`: selected when the Clipboard API is unavailable, so the user can copy manually.
 * - `data-copied` is set on the host for 3 seconds, so CSS can show a "copied" button state.
 * - `data-analytics-event`: sent to PostHog with `{ code }`.
 * @extends {Component}
 */
class CopyToClipboardComponent extends Component {
  /** @type {number | undefined} */
  #resetTimer;

  async copyToClipboard() {
    const copyContent = this.getAttribute('text-to-copy');

    if (!copyContent) return;

    try {
      await navigator.clipboard.writeText(copyContent);
    } catch {
      this.#selectTarget();
      this.#announce('errorMessage');
      return;
    }

    const copySuccessMessage = this.refs.copySuccessMessage;

    if (copySuccessMessage instanceof Element) {
      copySuccessMessage.classList.remove('visually-hidden');
    }

    this.#announce('successMessage');
    this.#flagCopied();
    this.#track(copyContent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimer);
  }

  /** @param {'successMessage' | 'errorMessage'} key */
  #announce(key) {
    const liveRegion = this.refs.liveRegion;

    if (!(liveRegion instanceof HTMLElement)) return;

    const message = liveRegion.dataset[key];

    if (!message) return;

    // Clear first so repeating the same message is announced again.
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  #flagCopied() {
    clearTimeout(this.#resetTimer);
    this.dataset.copied = 'true';

    this.#resetTimer = window.setTimeout(() => {
      delete this.dataset.copied;
    }, COPIED_DURATION);
  }

  #selectTarget() {
    const target = this.refs.copyTarget;

    if (!(target instanceof HTMLElement)) return;

    const range = document.createRange();
    range.selectNodeContents(target);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /** @param {string} code */
  #track(code) {
    const eventName = this.dataset.analyticsEvent;

    if (!eventName) return;

    window.phCapture?.(eventName, { code });
  }
}

if (!customElements.get('copy-to-clipboard-component')) {
  customElements.define('copy-to-clipboard-component', CopyToClipboardComponent);
}
